// src/pages/buyer/BuyerFinancePage.jsx
// Phase 4 — M4: Buyer Finance — Credit wallet, BNPL installment tracker, repayment
// Aesthetic: warm financial dashboard — saffron/rose on cream, Playfair headers, DM Sans body

import { useEffect, useState } from 'react'
import { financeApi } from '../../api/services'

const C = {
  pink:   '#FF3D7F',
  orange: '#FF8500',
  cream:  '#FFFBF5',
  ink:    '#1A1A2E',
  muted:  '#9CA3AF',
  green:  '#10B981',
  red:    '#EF4444',
  amber:  '#F59E0B',
}

// ── Tiny shared components ──────────────────────────────────────────────────

function Pill({ label, color = C.muted }) {
  return (
    <span style={{ background: `${color}18`, color, padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.03em' }}>
      {label}
    </span>
  )
}

function Stat({ label, value, sub, accent }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: '18px 20px', border: '1.5px solid #F0EDE8' }}>
      <p style={{ color: C.muted, fontSize: '0.74rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 6px' }}>{label}</p>
      <p style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.55rem', color: accent ?? C.ink, margin: 0 }}>{value}</p>
      {sub && <p style={{ color: C.muted, fontSize: '0.75rem', margin: '4px 0 0' }}>{sub}</p>}
    </div>
  )
}

function CreditMeter({ used, limit }) {
  const pct   = limit > 0 ? Math.min(100, (used / limit) * 100) : 0
  const color = pct >= 80 ? C.red : pct >= 60 ? C.amber : C.green
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '0.75rem', color: C.muted }}>
        <span>Used ₹{used?.toLocaleString('en-IN')}</span>
        <span>{Math.round(pct)}% utilised</span>
        <span>Limit ₹{limit?.toLocaleString('en-IN')}</span>
      </div>
      <div style={{ height: 10, background: '#F0EDE8', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${C.orange}, ${color})`, borderRadius: 6, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function BuyerFinancePage() {
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab]         = useState('overview')  // overview | installments | history
  const [applying, setApply]  = useState(false)
  const [repaying, setRepay]  = useState(null)  // installment id being paid
  const [payRef, setPayRef]   = useState('')

  useEffect(() => {
    financeApi.buyer.overview()
      .then(r => setData(r.data?.data ?? r.data))
      .finally(() => setLoading(false))
  }, [])

  async function applyCredit() {
    setApply(true)
    try {
      await financeApi.buyer.applyCredit()
      alert('Application submitted! We\'ll review and contact you within 2 business days.')
    } catch (e) {
      alert(e.response?.data?.message ?? 'Failed to submit application.')
    } finally { setApply(false) }
  }

  async function repayInstallment(id) {
    if (!payRef.trim()) return alert('Enter a payment reference.')
    try {
      await financeApi.buyer.repayInstallment(id, payRef)
      setRepay(null); setPayRef('')
      setData(null); setLoading(true)
      financeApi.buyer.overview().then(r => setData(r.data?.data ?? r.data)).finally(() => setLoading(false))
    } catch (e) {
      alert(e.response?.data?.message ?? 'Repayment failed.')
    }
  }

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, color: C.muted }}>
      Loading your finance dashboard…
    </div>
  )

  const account    = data?.credit_account
  const bnplOrders = data?.bnpl_orders ?? []
  const upcoming   = data?.upcoming_dues ?? []
  const overdue    = data?.overdue ?? []
  const outstanding = data?.total_outstanding ?? 0

  const hasAccount = account && account.status !== 'inactive'
  const isActive   = account?.status === 'active' && account?.kyc_status === 'verified'

  return (
    <div style={{ background: C.cream, minHeight: '100vh', padding: '28px 0' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '2rem', color: C.ink, margin: '0 0 4px' }}>
          Fabrinza Finance
        </h1>
        <p style={{ color: C.muted, margin: 0, fontSize: '0.88rem' }}>
          Your credit facility, Pay Later orders, and repayment schedule — all in one place.
        </p>
      </div>

      {/* Credit Account Card */}
      <div style={{ background: `linear-gradient(135deg, ${C.pink} 0%, ${C.orange} 100%)`, borderRadius: 24, padding: '24px 28px', marginBottom: 24, color: '#fff', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -30, right: -30, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
        <div style={{ position: 'absolute', bottom: -20, right: 60, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.75, margin: '0 0 8px' }}>Available Credit</p>
            <p style={{ fontFamily: 'Playfair Display, serif', fontWeight: 900, fontSize: '2.5rem', margin: 0, lineHeight: 1 }}>
              ₹{(account?.available_credit ?? 0).toLocaleString('en-IN')}
            </p>
            {isActive && (
              <p style={{ opacity: 0.75, fontSize: '0.82rem', margin: '6px 0 0' }}>
                of ₹{(account?.credit_limit ?? 0).toLocaleString('en-IN')} limit · {account?.max_installments} EMIs · {account?.repayment_days}-day terms
              </p>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <Pill
              label={isActive ? 'Active' : account?.status === 'inactive' ? 'Not Applied' : account?.status}
              color="#fff"
            />
            {!isActive && !hasAccount && (
              <button onClick={applyCredit} disabled={applying} style={{
                display: 'block', marginTop: 12, background: 'rgba(255,255,255,0.2)',
                border: '1.5px solid rgba(255,255,255,0.5)', color: '#fff',
                padding: '9px 20px', borderRadius: 12, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
              }}>
                {applying ? 'Applying…' : 'Apply for Credit'}
              </button>
            )}
            {account?.status === 'inactive' && account?.kyc_status === 'pending' && (
              <p style={{ fontSize: '0.78rem', opacity: 0.85, marginTop: 10 }}>Application under review</p>
            )}
          </div>
        </div>

        {isActive && (
          <div style={{ marginTop: 20 }}>
            <CreditMeter used={account.used_credit} limit={account.credit_limit} />
          </div>
        )}
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
        <Stat label="Total Outstanding" value={`₹${outstanding.toLocaleString('en-IN')}`} accent={outstanding > 0 ? C.red : C.green} />
        <Stat label="Overdue" value={overdue.length} sub="installments" accent={overdue.length > 0 ? C.red : C.ink} />
        <Stat label="Due This Week" value={upcoming.length} sub="upcoming payments" />
        <Stat label="Active BNPL" value={bnplOrders.filter(b => b.status === 'active').length} sub="orders" />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, background: '#fff', borderRadius: 14, padding: 6, border: '1.5px solid #F0EDE8', width: 'fit-content' }}>
        {[['overview', '📊 Overview'], ['installments', '📅 Installments'], ['history', '📋 BNPL Orders']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '8px 18px', borderRadius: 10, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
            border: 'none',
            background: tab === key ? `linear-gradient(135deg, ${C.pink}, ${C.orange})` : 'transparent',
            color: tab === key ? '#fff' : C.muted,
            transition: 'all 0.2s',
          }}>{label}</button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ──────────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div>
          {overdue.length > 0 && (
            <div style={{ background: '#FEF2F2', border: '1.5px solid #FECACA', borderRadius: 16, padding: '16px 20px', marginBottom: 20 }}>
              <h3 style={{ fontWeight: 800, color: C.red, margin: '0 0 12px', fontSize: '0.9rem' }}>
                🚨 {overdue.length} Overdue Installment{overdue.length > 1 ? 's' : ''}
              </h3>
              {overdue.map(inst => <InstallmentRow key={inst.id} inst={inst} onRepay={setRepay} urgent />)}
            </div>
          )}

          {upcoming.length > 0 && (
            <div style={{ background: '#fff', border: '1.5px solid #F0EDE8', borderRadius: 16, padding: '16px 20px' }}>
              <h3 style={{ fontWeight: 800, color: C.ink, margin: '0 0 12px', fontSize: '0.9rem' }}>📅 Upcoming (Next 14 Days)</h3>
              {upcoming.map(inst => <InstallmentRow key={inst.id} inst={inst} onRepay={setRepay} />)}
            </div>
          )}

          {overdue.length === 0 && upcoming.length === 0 && (
            <EmptyState icon="✅" title="All clear!" msg="No overdue or upcoming installments. You're in great shape." />
          )}
        </div>
      )}

      {/* ── INSTALLMENTS TAB ──────────────────────────────────────────────── */}
      {tab === 'installments' && (
        <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0EDE8', overflow: 'hidden' }}>
          {[...overdue, ...upcoming].length === 0
            ? <EmptyState icon="📅" title="No active installments" msg="All your BNPL installments are paid up." />
            : [...overdue, ...upcoming].map(inst => (
              <div key={inst.id} style={{ borderBottom: '1px solid #F0EDE8', padding: '16px 20px' }}>
                <InstallmentRow inst={inst} onRepay={setRepay} urgent={inst.status === 'overdue'} />
              </div>
            ))
          }
        </div>
      )}

      {/* ── BNPL ORDERS TAB ──────────────────────────────────────────────── */}
      {tab === 'history' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {bnplOrders.length === 0
            ? <EmptyState icon="📋" title="No Pay Later orders" msg="Use Fabrinza Pay Later at checkout to split your payments." />
            : bnplOrders.map(b => <BnplOrderCard key={b.id} bnpl={b} />)
          }
        </div>
      )}

      {/* ── Repayment Modal ───────────────────────────────────────────────── */}
      {repaying && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 28, maxWidth: 420, width: '100%' }}>
            <h3 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.2rem', color: C.ink, margin: '0 0 8px' }}>Mark Installment as Paid</h3>
            <p style={{ color: C.muted, fontSize: '0.83rem', margin: '0 0 20px' }}>Enter your payment reference (Razorpay ID, UTR, UPI Ref, etc.)</p>
            <input
              value={payRef}
              onChange={e => setPayRef(e.target.value)}
              placeholder="e.g. pay_XXXXXXXXXXXXXXX"
              style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #E5E7EB', borderRadius: 12, fontSize: '0.88rem', outline: 'none', marginBottom: 16, boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setRepay(null); setPayRef('') }} style={{ flex: 1, padding: '11px', borderRadius: 12, border: '1.5px solid #E5E7EB', background: '#fff', color: C.muted, fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem' }}>Cancel</button>
              <button onClick={() => repayInstallment(repaying)} style={{ flex: 2, padding: '11px', borderRadius: 12, border: 'none', background: `linear-gradient(135deg, ${C.pink}, ${C.orange})`, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem' }}>Confirm Payment</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Sub-components ──────────────────────────────────────────────────────────

function InstallmentRow({ inst, onRepay, urgent }) {
  const d    = Math.ceil((new Date(inst.due_date) - new Date()) / 86400000)
  const when = d < 0 ? `${Math.abs(d)} days overdue` : d === 0 ? 'Due today!' : `Due in ${d} days`
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '6px 0' }}>
      <div>
        <p style={{ fontWeight: 700, color: C.ink, margin: 0, fontSize: '0.88rem' }}>
          Installment #{inst.installment_number} — ₹{(inst.total_due ?? inst.amount)?.toLocaleString('en-IN')}
        </p>
        <p style={{ color: urgent ? C.red : C.amber, margin: '2px 0 0', fontSize: '0.76rem', fontWeight: 600 }}>{when}</p>
        {inst.penalty_amount > 0 && <p style={{ color: C.red, fontSize: '0.72rem', margin: '2px 0 0' }}>Late fee: ₹{inst.penalty_amount}</p>}
      </div>
      <button onClick={() => onRepay(inst.id)} style={{
        padding: '7px 16px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem',
        background: urgent ? C.red : `linear-gradient(135deg, ${C.pink}, ${C.orange})`, color: '#fff',
      }}>Pay Now</button>
    </div>
  )
}

function BnplOrderCard({ bnpl }) {
  const statusColor = { active: C.orange, paid: C.green, overdue: C.red, defaulted: C.red }
  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0EDE8', padding: '18px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <div>
          <p style={{ fontWeight: 800, color: C.ink, margin: '0 0 4px', fontSize: '0.92rem' }}>{bnpl.bnpl_number}</p>
          <p style={{ color: C.muted, fontSize: '0.78rem', margin: 0 }}>Order {bnpl.order?.order_number}</p>
        </div>
        <Pill label={bnpl.status} color={statusColor[bnpl.status] ?? C.muted} />
      </div>
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: '0.8rem', color: C.muted }}>
        <span>Principal: <b style={{ color: C.ink }}>₹{bnpl.principal?.toLocaleString('en-IN')}</b></span>
        <span>Fee: <b style={{ color: C.ink }}>₹{bnpl.processing_fee?.toLocaleString('en-IN')}</b></span>
        <span>Total: <b style={{ color: C.ink }}>₹{bnpl.total_payable?.toLocaleString('en-IN')}</b></span>
        <span>Paid: <b style={{ color: C.green }}>{bnpl.paid_installments}/{bnpl.total_installments}</b></span>
      </div>
      {bnpl.installments?.length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {bnpl.installments.map(inst => (
            <div key={inst.id} style={{
              padding: '4px 10px', borderRadius: 8, fontSize: '0.72rem', fontWeight: 700,
              background: inst.status === 'paid' ? '#D1FAE5' : inst.status === 'overdue' ? '#FEE2E2' : '#F3F4F6',
              color: inst.status === 'paid' ? C.green : inst.status === 'overdue' ? C.red : C.muted,
            }}>#{inst.installment_number} ₹{inst.amount?.toLocaleString('en-IN')} · {inst.status}</div>
          ))}
        </div>
      )}
    </div>
  )
}

function EmptyState({ icon, title, msg }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: 16, border: '1.5px solid #F0EDE8' }}>
      <p style={{ fontSize: '2.5rem', marginBottom: 10 }}>{icon}</p>
      <p style={{ fontWeight: 700, color: C.ink, marginBottom: 6 }}>{title}</p>
      <p style={{ color: C.muted, fontSize: '0.85rem' }}>{msg}</p>
    </div>
  )
}
